import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ZIndex } from "config/zIndex";
import { UNMEASURED_ANCHORED_POSITION } from "components/designV2/anchoredPosition.logic";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";
import { useAnchoredPosition } from "components/designV2/useAnchoredPosition";
import { Container, Label, Message } from "./searchableDropdown.styles";
import type { SearchableDropdownProps } from "./searchableDropdown.types";
import { useSearchableDropdown } from "./useSearchableDropdown";
import { SearchableDropdownInput } from "./searchableDropdownInput";
import { SearchableDropdownList } from "./searchableDropdownList";

export const SearchableDropdown = (
  props: SearchableDropdownProps,
): JSX.Element => {
  const { t } = useTranslation();
  const parentZIndex = useParentZIndex();
  const menuZIndex = parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown;
  const placeholderText = props.placeholder ?? t("General.SelectOption") ?? "";
  const hook = useSearchableDropdown(props, placeholderText);
  const menuPosition = useAnchoredPosition({
    triggerRef: hook.fieldRef,
    floatingRef: hook.listRef,
    isOpen: hook.isOpen,
  });

  return (
    <Container
      ref={hook.containerRef}
      aria-disabled={props.disabled}
      $size={hook.size}
      $fullWidth={props.fullWidth ?? false}
    >
      {props.label && <Label id={hook.labelId}>{props.label}</Label>}
      <SearchableDropdownInput
        label={props.label}
        labelId={hook.labelId}
        listboxId={hook.listboxId}
        placeholderText={hook.placeholderText}
        leadingIconName={props.leadingIconName}
        disabled={props.disabled}
        size={hook.size}
        visualState={hook.visualState}
        isOpen={hook.isOpen}
        highlightedIndex={hook.highlightedIndex}
        resolvedValue={hook.resolvedValue}
        showExtraCountBadge={hook.showExtraCountBadge}
        multiSelectDisplay={hook.multiSelectDisplay}
        canClear={hook.canClear}
        fieldRef={hook.fieldRef}
        inputRef={hook.inputRef}
        onOpenDropdown={hook.openDropdown}
        onCloseDropdown={hook.closeDropdown}
        onInputChange={hook.handleInputChange}
        onKeyDown={hook.handleKeyDown}
        onClear={(event) => {
          event.stopPropagation();
          if (props.disabled) return;
          props.onClear?.();
          hook.setQuery("");
          hook.clearHighlight();
        }}
        onToggleClick={() => {
          if (props.disabled) return;
          if (hook.isOpen) {
            hook.closeDropdown();
            return;
          }
          hook.openDropdown();
          hook.inputRef.current?.focus();
        }}
      />
      {(hook.visualState === "error" ? props.errorText : props.hintText) && (
        <Message $state={hook.visualState}>
          {hook.visualState === "error"
            ? (props.errorText ?? t("DesignComponents.DefaultError"))
            : props.hintText}
        </Message>
      )}
      {hook.isOpen &&
        createPortal(
          <SearchableDropdownList
            listRef={hook.listRef}
            listboxId={hook.listboxId}
            labelId={props.label ? hook.labelId : undefined}
            isOpen={hook.isOpen}
            left={(menuPosition ?? UNMEASURED_ANCHORED_POSITION).left}
            placement="portal"
            zIndex={menuZIndex}
            multiSelect={props.multiSelect}
            top={(menuPosition ?? UNMEASURED_ANCHORED_POSITION).top}
            filteredOptions={hook.filteredOptions}
            highlightedIndex={hook.highlightedIndex}
            selectedValueSet={hook.selectedValueSet}
            shouldHighlightOnHover={Boolean(props.shouldSelectOnEnterOrTab)}
            value={props.value}
            emptyText={props.emptyText ?? t("General.NoResults")}
            onSelect={hook.handleSelect}
            onHighlight={hook.handleMouseHighlight}
            width={(menuPosition ?? UNMEASURED_ANCHORED_POSITION).width}
          />,
          document.body,
        )}
    </Container>
  );
};
